import _ from 'underscore';
import $ from 'jquery';
import highchartsConfig from '../highcharts-config';
import template from '../template/adgroup-bidlandscape.pug';

import { AdGroupBidLandscapeViewModel } from './viewmodel';
import { BidLandscapeBaseView } from '../base-view';
import { BID_MODE } from './bid-mode';


/**
 * Keyword bid landscape
 * Display bid with estimated KPIs (click /impression) using both table & line chart.
 * It support display of two mode of data:
 *  default mode: update default adgroup bid without touching keyword cutomized bid
 *  uniform mode: update bid will override all cutomized keyword bid too
 */
export class AdGroupBidLandscape extends BidLandscapeBaseView {
  get events() {
    return _.defaults({
      'click .adgroup-bid-update-mode-div [data-value]': 'updateAdgroupSelectedMode',
    }, BidLandscapeBaseView.prototype.events);
  }

  /**
 * Initialize
 *
 * @param {object} params the params.
 * @param {object} params.entityType - the entity type of the entity
 * @param {object} params.parentItem - the enity info which showing the lanscape for
 * @param {String} params.parentItem.EntityName - EntityName of the entity, default 'adgroup'
 * @param {String} params.parentItem.Id - the id of the entity
 * @param {boolean} params.parentItem.isDSA - whether parent is belong to DSA campaign
 * @param {integer} params.parentItem.SuggestedNewValue - suggested bid value
 *   will mark as (suggested) at landscape graph, and selected by default
 *   if landscape points contains this bid value
 * @param {integer} params.parentItem.CurrentValue - current bid value,
 *   will mark as (current) at landscape graph, and selected when no suggested point
 *   if landscape points contains this bid value
 * @param {boolean} params.inSimpleMode - whether to show it in simple mode
 *   style in simple mode will be different
 * @param {object} params.dataToShow - the dataToShow for bid landscape,
 *  different type of bid landscape may have different data structure
 * @param {object} params.i18n - the localization support for bid landscape
 * @param {object} params.i18n.getString - function to get localized string
 * @param {object} params.i18n.formatDecimalToInteger - format decimal to localized integer
 * @param {object} params.i18n.formatDecimalToFixed - format decimal to 2 digits localized integer
 * @param {object} params.i18n.parseDecimal - convert localized number to decimal
 * @param {object} params.i18n.isValidDecimal - return whether string is valid decimal
 * @param {boolean} inKeywordCreationWorkflow -
 *  indicate if this component show on keyword creation workflow
 * @return {Void} void
 */
  initialize({
    parentItem = {},
    i18n,
    helpId = null,
    entityType = 'adgroup',
    inSimpleMode = false,
    dataToShow = {},
    isForVNext,
    inKeywordCreationWorkflow = false,
  }) {
    super.initialize({
      parentItem,
      i18n,
      entityType,
      inSimpleMode,
      dataToShow,
      isForVNext,
      inKeywordCreationWorkflow,
    });

    this.helpId = helpId;
    this.i18n = i18n;
    this.isDSA = parentItem.isDSA;
    this.campaignType = parentItem.campaignType || 'Default';
    this.inKeywordCreationWorkflow = inKeywordCreationWorkflow;

    this.viewModel.adgroupSelectMode.subscribe(() => {
      this.trigger('selectModeChange');
    });
  }

  get ViewModel() {
    return AdGroupBidLandscapeViewModel;
  }

  get template() {
    return () => template(_.extend(this.viewModel, {
      isForVNext: this.isForVNext,
      helpId: this.helpId,
      updateWithOverWriteWarning: this.i18n.getString(this.isDSA ? 'Adgroup_BidLandscape_UpdateWithOverwrite_DSA' : 'Adgroup_BidLandscape_UpdateWithOverwrite')
        .replace('{0}', this.i18n.formatDecimalToInteger(1000)),
      BID_MODE,
      updateWithoutOverWrite: this.i18n.getString(this.isDSA ? 'Adgroup_BidLandscape_UpdateWithOutOverwrite_DSA' : 'Adgroup_BidLandscape_UpdateWithOutOverwrite'),
      uniformSaveWarnFooter: this.i18n.getString(this.isDSA ? 'AdGroupBidLandscape_Uniform_SaveWarnFooter_DSA' : 'AdGroupBidLandscape_Uniform_SaveWarnFooter'),
      campaignType: this.campaignType,
      inKeywordCreationWorkflow: this.inKeywordCreationWorkflow,
      subTitle: this.inKeywordCreationWorkflow ?
        this.i18n.getString('Creation_Workflow_Bid_Landscape_Description') :
        this.i18n.getString('SelectOrInputValueText'),
    }));
  }

  // update selected mode
  updateAdgroupSelectedMode(event) {
    this.viewModel.updateAdgroupSelectedMode($(event.currentTarget).attr('data-value'));
  }

  isAudienceAdgroupCPMCPV() {
    return this.viewModel.bidType === 'CPM' || this.viewModel.bidType === 'CPV';
  }

  // override base for CPM/CPV
  prepareChartTitles() {
    if (this.isAudienceAdgroupCPMCPV()) {
      this.slideChartOptions.yAxis.title.text = this.options.i18n.getString('KPI_EstCost').toUpperCase();
      if (this.viewModel.bidType === 'CPM') {
        this.slideChartOptions.xAxis.title.text = this.options.i18n.getString('KPI_EstImp').toUpperCase();
      } else {
        this.slideChartOptions.xAxis.title.text = this.options.i18n.getString('KPI_EstView').toUpperCase();
      }
    } else {
      super.prepareChartTitles();
    }
  }

  // override base for CPM/CPV
  convertPoint(point) {
    const graphPoint = super.convertPoint(point);

    if (this.isAudienceAdgroupCPMCPV()) {
      graphPoint.y = point.cost;
      graphPoint.yStr = point.costStr;
      if (this.viewModel.bidType === 'CPM') {
        graphPoint.x = point.impressions;
      } else {
        graphPoint.x = point.view;
      }
    }

    return graphPoint;
  }

  prepareExtraSeries() {
    if (this.viewModel.clearBid()) {
      const graphPoint = _.extend(highchartsConfig.getAdgroupCurrentPointSetting(), {
        x: this.viewModel.isClickGraph() ?
          this.viewModel.customBidCurrentPoint().clicks :
          this.viewModel.customBidCurrentPoint().bid,
        y: this.viewModel.isClickGraph() ?
          this.viewModel.customBidCurrentPoint().cost :
          this.viewModel.customBidCurrentPoint().impressions,
        index: 0, // index in tableData
      });
      if (this.isAudienceAdgroupCPMCPV()) {
        graphPoint.y = this.viewModel.customBidCurrentPoint().cost;
        graphPoint.yStr = this.viewModel.customBidCurrentPoint().costStr;
        if (this.viewModel.bidType === 'CPM') {
          graphPoint.x = this.viewModel.customBidCurrentPoint().impressions;
        } else {
          graphPoint.x = this.viewModel.customBidCurrentPoint().view;
        }
      }

      return [graphPoint];
    }

    return null;
  }

  clearBid() {
    return this.viewModel.clearBid();
  }
}
